import { useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';

function mergeDomains(domains: string[], input: string): string[] {
  const additions = input
    .split(/[,;\s]+/)
    .filter(Boolean)
    .map(value => value.toLowerCase().replace(/^@/, ''));
  for (const domain of additions) {
    const labels = domain.split('.');
    if (
      domain.length > 253 ||
      labels.length < 2 ||
      labels.some(label => !/^[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?$/u.test(label))
    ) {
      throw new Error(
        `Enter a domain such as hospital.org, without a full email address or URL. Check "${domain}".`
      );
    }
  }
  return [...new Set([...domains, ...additions])];
}

export function useDomainDraft(saved: string[]) {
  const [draft, setDraft] = useState<string[] | null>(null);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const domains = draft ?? saved;

  const prepareSave = (): string[] | null => {
    try {
      const values = mergeDomains(domains, input);
      setError(null);
      return values;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Enter a valid domain.');
      return null;
    }
  };

  return {
    domains,
    input,
    error,
    hasChanges: input.trim().length > 0 || domains.join('\n') !== saved.join('\n'),
    setInput: (value: string) => {
      setInput(value);
      setError(null);
    },
    add: () => {
      const values = prepareSave();
      if (values === null) return;
      setDraft(values);
      setInput('');
    },
    remove: (domain: string) => setDraft(domains.filter(value => value !== domain)),
    prepareSave,
    reset: () => {
      setDraft(null);
      setInput('');
      setError(null);
    },
  };
}

export function DomainChipsInput({
  editor,
  disabled = false,
  hideLabel = false,
}: {
  editor: ReturnType<typeof useDomainDraft>;
  disabled?: boolean;
  hideLabel?: boolean;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-3">
      <Label htmlFor={id} className={hideLabel ? 'sr-only' : undefined}>
        Allowed email domains
      </Label>
      {editor.domains.length > 0 ? (
        <ul aria-label="Allowed domains" className="flex flex-wrap gap-2">
          {editor.domains.map(domain => (
            <li
              key={domain}
              className="flex max-w-full items-center gap-1 rounded-3xl border bg-muted px-3 py-1 text-sm"
            >
              <span className="break-all">{domain}</span>
              <button
                type="button"
                aria-label={`Remove ${domain}`}
                disabled={disabled}
                className="shrink-0 rounded-3xl p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                onClick={() => editor.remove(domain)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">No allowed domains yet.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Input
          id={id}
          ref={inputRef}
          value={editor.input}
          disabled={disabled}
          className="min-w-0 flex-1 basis-40"
          placeholder="hospital.org"
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={false}
          aria-describedby={`${id}-help${editor.error ? ` ${id}-error` : ''}`}
          aria-invalid={!!editor.error}
          onChange={event => editor.setInput(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
              event.preventDefault();
              editor.add();
            }
          }}
          onPaste={event => {
            const pasted = event.clipboardData.getData('text');
            if (!/[\r\n]/.test(pasted)) return;
            event.preventDefault();
            const start = event.currentTarget.selectionStart ?? editor.input.length;
            const end = event.currentTarget.selectionEnd ?? start;
            editor.setInput(
              editor.input.slice(0, start) +
                pasted.replace(/[\r\n]+/g, ', ') +
                editor.input.slice(end)
            );
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={disabled || !editor.input.trim()}
          onClick={() => {
            editor.add();
            inputRef.current?.focus();
          }}
        >
          Add domain
        </Button>
      </div>
      <p id={`${id}-help`} className="text-xs text-muted-foreground">
        Add one domain at a time with Enter or Add domain. You can also paste several domains
        separated by commas or new lines.
      </p>
      {editor.error && (
        <p id={`${id}-error`} role="alert" className="text-sm text-destructive">
          {editor.error}
        </p>
      )}
    </div>
  );
}
